import {
  TenantConfig,
  tenantConfigSchema,
  defaultTenantConfig,
} from "./tenant.schema";

type TenantModule = { default: unknown };

const productConfigs = import.meta.glob<TenantModule>(
  "../products/*/tenant.json",
  { eager: false },
);

const clientConfigs = import.meta.glob<TenantModule>(
  "../products/*/*/tenant.json",
  { eager: false },
);

function deepMerge<T extends Record<string, unknown>>(
  base: T,
  override: Partial<T>,
): T {
  const result = { ...base } as T;

  for (const key of Object.keys(override) as Array<keyof T>) {
    const baseValue = base[key];
    const overrideValue = override[key];

    if (
      overrideValue !== null &&
      typeof overrideValue === "object" &&
      !Array.isArray(overrideValue) &&
      baseValue !== null &&
      typeof baseValue === "object" &&
      !Array.isArray(baseValue)
    ) {
      result[key] = deepMerge(
        baseValue as Record<string, unknown>,
        overrideValue as Record<string, unknown>,
      ) as T[keyof T];
    } else if (overrideValue !== undefined) {
      result[key] = overrideValue as T[keyof T];
    }
  }

  return result;
}

export async function loadTenantConfig(
  productId: string,
  clientId?: string,
): Promise<TenantConfig> {
  let config = defaultTenantConfig(productId);

  const productPath = `../products/${productId}/tenant.json`;
  const productLoader = productConfigs[productPath];

  if (productLoader) {
    try {
      const module = await productLoader();
      const parsed = tenantConfigSchema.safeParse(module.default);

      if (parsed.success) {
        config = deepMerge(config, parsed.data);
      }
    } catch {
      console.info(`[Tenant] Failed to load product config for "${productId}"`);
    }
  }

  if (clientId) {
    const clientPath = `../products/${productId}/${clientId}/tenant.json`;
    const clientLoader = clientConfigs[clientPath];

    if (clientLoader) {
      try {
        const module = await clientLoader();
        const parsed = tenantConfigSchema.safeParse(module.default);

        if (parsed.success) {
          config = deepMerge(config, parsed.data);
        }
      } catch {
        console.info(
          `[Tenant] Failed to load client config for "${productId}/${clientId}"`,
        );
      }
    }
  }

  return config;
}
